/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';

import { tPriceId, tProductKind, tProductWeight } from './product';

export const tBrandCondition = t.string;
export const tBrandIdCondition = t.number;

export const tKindCondition = t.intersection([
  t.type({ kind: tProductKind }),
  t.partial({ brand_subtype: t.string }),
  t.partial({ root_subtype: t.string }),
]);

export const tWeightCondition = tProductWeight;

export const tProductThresholdConditions = t.partial({
  maximum_price: t.union([t.number, t.undefined, t.null]),
  minimum_price: t.union([t.number, t.undefined, t.null]),
});

const tWeightValueCondition = t.type({
  units: t.string,
  value: t.number,
});

const tProductConditions = t.partial({
  brands: t.union([t.array(tBrandCondition), t.undefined]),
  brand_ids: t.union([t.array(tBrandIdCondition), t.undefined]),
  excluded_product_ids: t.union([t.array(t.number), t.undefined]),
  kinds: t.union([t.array(tKindCondition), t.undefined]),
  weights: t.union([t.array(tWeightCondition), t.undefined]),
  weight_values: t.union([t.array(tWeightValueCondition), t.undefined]),
  included_product_ids: t.union([t.array(t.number), t.undefined, t.null]),
  product_threshold: t.union([tProductThresholdConditions, t.undefined]),
  lineage: t.union([t.undefined, t.string]),
  lineages: t.union([t.undefined, t.array(t.string)]),
});

const tDependentBundleConditions = t.intersection([
  tProductConditions,
  t.type({
    max_number_of_discounted_products: t.union([t.number, t.null]),
  }),
]);

const tBundleProductConditions = t.partial({
  independent: t.intersection([
    tProductConditions,
    t.type({
      threshold_number_of_items_in_cart: t.union([t.number, t.null]),
    }),
  ]),
  dependent: tDependentBundleConditions,
  settings: t.type({
    allow_discounts_on_required_products: t.union([t.boolean, t.null]),
    apply_target_price_per_unit: t.union([t.boolean, t.null]),
  }),
  max_applications_per_cart: t.union([t.number, t.null]),
});

const tCartTotalConditions = t.intersection([
  t.partial({
    reservation_modes: t.union([
      t.partial({
        kiosk: t.union([t.boolean, t.undefined]),
        delivery: t.union([t.boolean, t.undefined]),
        pickup: t.union([t.boolean, t.undefined]),
      }),
      t.undefined,
    ]),
  }),
  t.type({
    threshold: t.union([t.number, t.string]),
  }),
]);

const tSeniorGroupConditions = t.type({
  type: t.literal('senior'),
  required_age: t.number,
});

const tPediatricGroupConditions = t.type({
  type: t.literal('pediatric'),
  required_age: t.number,
});

const tQualifiedGroupConditions = t.union([
  tSeniorGroupConditions,
  tPediatricGroupConditions,
  t.type({
    type: t.keyof({
      student: null,
      veteran: null,
      medical: null,
      ssi: null,
      snap: null,
      industry: null,
      medicare: null,
      medicaid: null,
      member: null,
      locals: null,
      chip: null,
      pace: null,
      pacenet: null,
      wic: null,
      'PA Gov': null,
    }),
  }),
]);

const tAdditionalProductWeight = t.keyof({
  three_gram: null,
  four_gram: null,
  five_gram: null,
  eight_gram: null,
  ten_gram: null,
  two_ounce: null,
  two_and_half_ounce: null,
});

const tBulkWeightCondition = t.intersection([
  t.type({
    price_id: tPriceId,
  }),
  tProductThresholdConditions,
]);

const tBulkPricingConditions = t.partial({
  brands: t.union([t.array(tBrandCondition), t.undefined]),
  brand_ids: t.union([t.array(tBrandIdCondition), t.undefined]),
  kinds: t.union([t.array(tKindCondition), t.undefined]),
  excluded_product_ids: t.union([t.array(t.number), t.undefined]),
  included_product_ids: t.union([t.array(t.number), t.undefined, t.null]),
  lineage: t.union([t.undefined, t.string]),
  bulk_weights: t.union([t.array(tBulkWeightCondition), t.undefined]),
  target_weight: t.union([
    tPriceId,
    t.union([tAdditionalProductWeight, t.undefined]),
  ]),
  max_applications_per_cart: t.union([t.number, t.null]),
});

export const tSpecialConditions = t.partial({
  product: tProductConditions,
  cart_total: tCartTotalConditions,
  qualified_group: tQualifiedGroupConditions,
  bundle: tBundleProductConditions,
  bulk_pricing: tBulkPricingConditions,
  created_at: t.union([t.string, t.null]),
  updated_at: t.union([t.string, t.null]),
});
